import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileUp, Loader2, Layers } from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import { useAuthContext } from "../../../components/auth/AuthProvider";

interface DocumentDropzoneProps {
  /** Initial layout selection. Empty string / undefined → auto-detect. */
  defaultLayoutId?: string;
  /**
   * Fired after every successful upload (the backend accepted the file and
   * returned a `process_id`). The page hosting the dropzone uses this to
   * auto-close its upload modal once the first file is on its way.
   */
  onUploaded?: (info: { fileName: string; processId?: string }) => void;
  /**
   * Which backend pipeline the upload routes to.
   *   "tracking"     → POST /manual_upload (default, full OCR pipeline).
   *                    Layout options come from /tracking_search_options.
   *   "configStudio" → POST /config_studio/manual_upload (Layout Config inbox).
   *                    Layout options are the AUTO_LAYOUT / MANUAL_LAYOUT
   *                    sentinels per the reference tools/index.jsx upload.
   * Mirrors the two upload flows in the reference — `manualUpload` for
   * OCR Tracking vs `toolsDocumentUpload` for the Layout Config screen.
   */
  mode?: "tracking" | "configStudio";
}

// Tracking-mode formats remain the original OCR set; configStudio matches
// the Layout Config reference design (pdf / docx / zip / images).
const ACCEPTED_TRACKING = ".pdf,.tif,.tiff,.png,.jpg,.jpeg";
const ACCEPTED_CONFIG_STUDIO =
  ".pdf,.docx,.zip,.png,.jpg,.jpeg,.tif,.tiff";
const AUTO_DETECT = ""; // sentinel: backend treats missing layoutId as auto-detect
// Hardcoded layout options for the config-studio upload — matches the
// reference's UploadModal `layouts` prop in tools/index.jsx. AUTO_LAYOUT
// runs auto-detect on the document; MANUAL_LAYOUT routes it to manual
// layout configuration in the cockpit.
const CONFIG_STUDIO_LAYOUTS = ["AUTO_LAYOUT", "MANUAL_LAYOUT"] as const;
const CONFIG_STUDIO_DEFAULT_LAYOUT = "AUTO_LAYOUT";

export function DocumentDropzone({ defaultLayoutId, onUploaded, mode = "tracking" }: DocumentDropzoneProps) {
  const isConfigStudio = mode === "configStudio";
  const [dragOver, setDragOver] = useState(false);
  // For configStudio the default is AUTO_LAYOUT (per reference); for the
  // OCR-tracking dropzone the empty-string sentinel means "auto-detect".
  const initialLayout =
    defaultLayoutId ?? (isConfigStudio ? CONFIG_STUDIO_DEFAULT_LAYOUT : AUTO_DETECT);
  const [layoutId, setLayoutId] = useState<string>(initialLayout);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  const uploaderName: string =
    (user?.profile?.preferred_username as string | undefined) ??
    (user?.profile?.email as string | undefined) ??
    "doit-v2";

  // Layout options come from /tracking_search_layout_id — a dedicated
  // endpoint that returns just the layout-id list for the upload
  // dropdown (vs. /tracking_search_options which returns the full
  // filter palette). Mirrors the reference's manual-upload-form.jsx
  // which calls `fetchTrackingSearchLayoutId()`. The
  // `AUTO_LAYOUT_DETECTION` sentinel is filtered out below because we
  // already render an explicit "Auto-detect" option at the top.
  //
  // Disabled in configStudio mode — the Layout Config upload uses the
  // hardcoded [AUTO_LAYOUT, MANUAL_LAYOUT] pair instead, matching the
  // reference UploadModal usage in tools/index.jsx.
  const layoutsQuery = useQuery({
    queryKey: ["ocr", "upload-layouts"],
    queryFn: () => ocrApi.trackingSearchLayoutId(),
    enabled: !isConfigStudio,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      if (isConfigStudio) {
        // configStudio picker only exposes the AUTO_LAYOUT / MANUAL_LAYOUT
        // sentinels (see CONFIG_STUDIO_LAYOUTS) — pass the selection
        // straight through so the backend routes the doc accordingly.
        return ocrApi.toolsDocumentUpload({
          file,
          layoutId: layoutId || CONFIG_STUDIO_DEFAULT_LAYOUT,
          uploader: uploaderName,
        });
      }
      return ocrApi.uploadFile({
        file,
        layoutId: layoutId || undefined,
        uploader: uploaderName,
      });
    },
    onSuccess: (data, file) => {
      // Success detection — the backend has two response shapes:
      //   (a) Async accepted: `{message: "...background...", process_id: "..."}`
      //       — no `status` field at all. This is the modern path.
      //   (b) Legacy sync:    `{status: "success", documentId?: "..."}`
      //   (c) Error envelope: `{status: "error", error|message: "..."}` or
      //       FastAPI's pydantic 422 (caught by onError below).
      // A 2xx response with no explicit error indicator → treat as success.
      const isErrorEnvelope =
        data.status === "error" || (data.error != null && data.error !== "");
      const processId = data.process_id;
      if (!isErrorEnvelope) {
        const detailBits: string[] = [];
        if (processId) detailBits.push(`process ${processId.slice(0, 8)}…`);
        if (data.documentId) detailBits.push(`id ${data.documentId}`);
        if (layoutId) detailBits.push(`layout ${layoutId}`);
        toast.success(`Uploaded ${file.name}`, {
          description: detailBits.length
            ? detailBits.join(" · ")
            : data.message,
        });
        // Invalidate only the inbox that received the upload. The
        // SmartInbox cache prefix is ["ocr", "tracking", dataSource, …]
        // so we narrow by mode → "configStudio" vs "tracking".
        const inboxKey = [
          "ocr",
          "tracking",
          isConfigStudio ? "configStudio" : "tracking",
        ] as const;
        queryClient.invalidateQueries({ queryKey: inboxKey, exact: false });
        queryClient.invalidateQueries({ queryKey: ["ocr", "dashboard"] });

        // /config_studio/manual_upload returns "Request is being processed
        // in the background" — the backend writes the row to
        // config_studio_tracking some time later (and broadcasts a WS
        // insert frame for it). Poll a few times after the upload so the
        // row shows up even when WS is offline or the broadcast misses.
        if (isConfigStudio) {
          for (const ms of [2000, 5000, 10000]) {
            window.setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: inboxKey,
                exact: false,
              });
            }, ms);
          }
        }

        onUploaded?.({ fileName: file.name, processId });
      } else {
        toast.error(`Upload failed: ${file.name}`, {
          description: data.error || data.message,
        });
      }
    },
    onError: (err: unknown, file) => {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Upload failed: ${file.name}`, { description: message });
    },
  });

  const handleFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      const arr = Array.from(files);
      if (arr.length === 0) return;
      // configStudio is a single-file flow per the Layout Config design;
      // tracking mode keeps the multi-file behavior. Tracking mode also
      // accepts uploads without a layout selected — the backend
      // auto-classifies in that case (the "Auto-detect" option below).
      if (isConfigStudio) {
        uploadMutation.mutate(arr[0]);
      } else {
        arr.forEach((file) => uploadMutation.mutate(file));
      }
    },
    [uploadMutation, isConfigStudio],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer?.files ?? null);
    },
    [handleFiles],
  );

  const onClick = useCallback(() => inputRef.current?.click(), []);

  // Choose the dropdown source per mode. configStudio uses the fixed
  // [AUTO_LAYOUT, MANUAL_LAYOUT] pair the reference upload modal exposes;
  // tracking mode pulls from /tracking_search_layout_id and prepends an
  // "Auto-detect" option — picking it leaves layout_id empty so the
  // backend classifier runs.
  const layouts: string[] = isConfigStudio
    ? [...CONFIG_STUDIO_LAYOUTS]
    : (layoutsQuery.data ?? []).filter(
        // Drop the auto-detect sentinels — we already render an explicit
        // "Auto-detect" option at the top of the dropdown, so showing
        // them again would duplicate the entry. Both names exist because
        // older deployments returned "AUTO_LAYOUT_DETECTION" while the
        // current backend sends "AUTO_LAYOUT".
        (id) =>
          id && id !== "AUTO_LAYOUT_DETECTION" && id !== "AUTO_LAYOUT",
      );
  const resetValue = isConfigStudio ? CONFIG_STUDIO_DEFAULT_LAYOUT : AUTO_DETECT;
  const isDirty = layoutId !== resetValue;

  const acceptedTypes = isConfigStudio
    ? ACCEPTED_CONFIG_STUDIO
    : ACCEPTED_TRACKING;

  // The configStudio design (Layout Config) places the LAYOUT label to the
  // left of a wide select and adds a separate "File Name" label above the
  // dropzone, with single-file copy and pdf/docx/zip/images accepted. The
  // tracking design keeps the compact pill-style picker with a Reset button
  // and multi-file copy. We branch the markup so each flow stays clean.
  if (isConfigStudio) {
    return (
      <div className="space-y-5">
        {/* LAYOUT row — label above the select, doit's standard rounded-md
            bordered input style (matches LayoutEditorModal / AttributeFormModal).
            Workflow picker is intentionally absent here; we still send
            workflow_id under the hood (required by /config_studio/manual_upload),
            auto-selected from the first available workflow. */}
        <div>
          <label
            htmlFor="dropzone-layout"
            className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium text-gray-700"
          >
            <Layers className="h-3.5 w-3.5 text-gray-400" />
            Layout
          </label>
          <select
            id="dropzone-layout"
            value={layoutId}
            onChange={(e) => setLayoutId(e.target.value)}
            disabled={uploadMutation.isPending}
            className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
          >
            {layouts.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>

        {/* File Name label above the dropzone. */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">
            File Name
          </label>
          <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-12 transition cursor-pointer outline-none ${
              dragOver
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept={acceptedTypes}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />

            <div className="rounded-full border border-gray-200 bg-white p-4 text-blue-600 shadow-sm">
              {uploadMutation.isPending ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : dragOver ? (
                <FileUp className="h-7 w-7" />
              ) : (
                <Upload className="h-7 w-7" />
              )}
            </div>

            <div className="text-center">
              <p className="text-base font-semibold text-gray-900">
                {uploadMutation.isPending
                  ? "Uploading…"
                  : dragOver
                  ? "Drop to upload"
                  : "Upload document"}
              </p>
              {!uploadMutation.isPending && !dragOver ? (
                <p className="text-base font-semibold text-blue-600">
                  (single file)
                </p>
              ) : null}
              <p className="mt-3 text-sm text-gray-400">
                (Allowed: pdf, docx, zip, images)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tracking mode (default) — unchanged compact pill picker + multi-file zone.
  return (
    <div className="space-y-2">
      {/* Layout picker — placed outside the dropzone so clicking the select
          doesn't trigger the hidden file input. */}
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
        <Layers className="h-4 w-4 flex-shrink-0 text-gray-400" />
        <label
          htmlFor="dropzone-layout"
          className="text-xs font-medium text-gray-600"
        >
          Layout
        </label>
        <select
          id="dropzone-layout"
          value={layoutId}
          onChange={(e) => setLayoutId(e.target.value)}
          disabled={layoutsQuery.isLoading || uploadMutation.isPending}
          className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value={AUTO_DETECT}>
            {layoutsQuery.isLoading ? "Loading layouts…" : "Auto-detect"}
          </option>
          {layouts.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
        {isDirty ? (
          <button
            type="button"
            onClick={() => setLayoutId(resetValue)}
            className="text-xs text-gray-500 hover:text-gray-700"
            title="Reset to auto-detect"
          >
            Reset
          </button>
        ) : null}
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 transition cursor-pointer outline-none ${
          dragOver
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptedTypes}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="rounded-full bg-blue-100 p-4 text-blue-600">
          {uploadMutation.isPending ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : dragOver ? (
            <FileUp className="h-8 w-8" />
          ) : (
            <Upload className="h-8 w-8" />
          )}
        </div>

        <div className="text-center">
          <p className="text-base font-semibold text-gray-900">
            {uploadMutation.isPending
              ? "Uploading…"
              : dragOver
              ? "Drop to upload"
              : "Drop a document, or click to browse"}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            PDF, TIFF, PNG, JPG · multiple files supported
          </p>
          <p className="mt-2 text-xs text-gray-400">
            {layoutId ? (
              <>
                Will be processed with layout{" "}
                <span className="font-mono text-gray-600">{layoutId}</span>
              </>
            ) : (
              "Will be auto-classified by the backend"
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
